import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";
import { toast } from 'react-toastify';
import Select from 'react-select';
import { IoChevronBackOutline } from "react-icons/io5";
import CardSelector from '../components/Card/CardSelector';
import { GrNotes } from "react-icons/gr";

const customStyles = {
    control: (provided) => ({
        ...provided,
        backgroundColor: "black",
        borderColor: "#292524",
        color: "white",
        minHeight: "40px",
        fontSize: "1rem",
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "white",
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: "black",
        zIndex: '10'
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#1f2937" : "black",
        color: "white",
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "white",
    }),
    input: (provided) => ({
        ...provided,
        color: "white",
    }),
};

function getPosMap(handInt) {
    const posMap = [];
    posMap[2] = ['SB', 'BB'];
    posMap[3] = ['SB', 'BB', 'BTN'];
    posMap[4] = ['SB', 'BB', 'UTG', 'BTN'];
    posMap[5] = ['SB', 'BB', 'UTG', 'CO', 'BTN'];
    posMap[6] = ['SB', 'BB', 'UTG', 'HJ', 'CO', 'BTN'];
    posMap[7] = ['SB', 'BB', 'UTG', 'UTG+1', 'HJ', 'CO', 'BTN'];
    posMap[8] = ['SB', 'BB', 'UTG', 'UTG+1', 'LJ', 'HJ', 'CO', 'BTN'];
    posMap[9] = ['SB', 'BB', 'UTG', 'UTG+1', 'MP1', 'LJ', 'HJ', 'CO', 'BTN'];
    posMap[10] = ['SB', 'BB', 'UTG', 'UTG+1', 'MP1', 'MP2', 'LJ', 'HJ', 'CO', 'BTN'];
    return posMap[handInt] || [];
}

const handedOptions = [...Array(9).keys()].map(i => ({
    value: i + 2,
    label: `${i + 2}`
}));

const mapToSelectOption = (value, options) =>
    options.find(opt => opt.value === value) || null;

function parseCards(input) {
    if (!input || input.length < 2) return {};
    const cards = input.match(/.{1,2}/g) || [];
    const result = {};
    cards.forEach((card, index) => {
        result[`value${index + 1}`] = card[0];
        result[`type${index + 1}`] = card[1];
    });
    return result;
}

function AddHand() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { prevHand = {} } = location.state || {};
    const initialHanded = prevHand.handed;
    const initialHandedOption = handedOptions.find(opt => parseInt(opt.value) === parseInt(initialHanded)) || handedOptions[6];
    const initialPositions = getPosMap(initialHanded);
    const positionOptions = initialPositions.map(pos => ({ label: pos, value: pos }));
    const [showNotes, setShowNotes] = useState(false);
    const [state, setState] = useState({
        session: id,
        handed: initialHandedOption,
        smallBlind: prevHand.sb || '100',
        bigBlind: prevHand.bb || '200',
        ante: prevHand.ante || 0,
        stackSize: prevHand.stackSize || '0',
        position: positionOptions,
        selectedPosition: mapToSelectOption(prevHand.position, positionOptions) || positionOptions[0] || null,
        bbEditable: false,
        error: false,
        loading: false,
        cardSelection: parseCards(prevHand.holding),
        boardSelection: parseCards(prevHand.board),
        notes: prevHand?.notes
    });

    useEffect(() => {
        const updatedOptions = getPosMap(state.handed.value).map(pos => ({ label: pos, value: pos }));
        const matched = mapToSelectOption(prevHand.position, updatedOptions);
        setState(prev => ({
            ...prev,
            position: updatedOptions,
            selectedPosition: matched || updatedOptions[0] || null,
        }));
    }, [state.handed, prevHand.position]);
 
    const usedCards = [
        ...(state.cardSelection.value1 && state.cardSelection.type1 ? [`${state.cardSelection.value1}${state.cardSelection.type1}`] : []),
        ...(state.cardSelection.value2 && state.cardSelection.type2 ? [`${state.cardSelection.value2}${state.cardSelection.type2}`] : []),
        ...(state.boardSelection.value1 && state.boardSelection.type1 ? [`${state.boardSelection.value1}${state.boardSelection.type1}`] : []),
        ...(state.boardSelection.value2 && state.boardSelection.type2 ? [`${state.boardSelection.value2}${state.boardSelection.type2}`] : []),
        ...(state.boardSelection.value3 && state.boardSelection.type3 ? [`${state.boardSelection.value3}${state.boardSelection.type3}`] : []),
        ...(state.boardSelection.value4 && state.boardSelection.type4 ? [`${state.boardSelection.value4}${state.boardSelection.type4}`] : []),
        ...(state.boardSelection.value5 && state.boardSelection.type5 ? [`${state.boardSelection.value5}${state.boardSelection.type5}`] : []),
    ];

    const handleBack = () => navigate(-1);

    const handleHandedChange = (option) => {
        const handInt = parseInt(option.value);
        const newPositions = getPosMap(handInt).map(pos => ({ label: pos, value: pos }));
        setState(prevState => ({
            ...prevState,
            handed: option,
            position: newPositions,
            selectedPosition: newPositions[0] || null
        }));
    };

    const handlePositionChange = (option) => {
        setState(prevState => ({
            ...prevState,
            selectedPosition: option
        }));
    };

    const handleSmallBlindChange = (e) => {
        const sbValue = e.target.value;
        setState(prevState => ({
            ...prevState,
            smallBlind: sbValue
        }));
        if (!state.bbEditable) {
            setState(prevState => ({
                ...prevState,
                bigBlind: sbValue * 2,
            }));
        }
    };

    const handleBigBlindChange = (e) => {
        setState(prevState => ({
            ...prevState,
            bigBlind: e.target.value,
            bbEditable: true,
        }));
    };

    const handleCardSelectionChange = (val) => {
        setState(prev => ({ ...prev, cardSelection: val }));
    };

    const handleBoardSelectionChange = (val) => {
        setState(prev => ({ ...prev, boardSelection: val }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const holding = `${state.cardSelection.value1 || ''}${state.cardSelection.type1 || ''}${state.cardSelection.value2 || ''}${state.cardSelection.type2 || ''}`;
        const board =
            (state.boardSelection.value1 || '') + (state.boardSelection.type1 || '') +
            (state.boardSelection.value2 || '') + (state.boardSelection.type2 || '') +
            (state.boardSelection.value3 || '') + (state.boardSelection.type3 || '') +
            (state.boardSelection.value4 || '') + (state.boardSelection.type4 || '') +
            (state.boardSelection.value5 || '') + (state.boardSelection.type5 || '');

        const postData = {
            sessionId: parseInt(state.session),
            handed: parseInt(state.handed.value),
            sb: parseInt(state.smallBlind),
            bb: parseInt(state.bigBlind),
            ante: parseInt(state.ante),
            stack: parseInt(state.stackSize),
            position: state.selectedPosition.value,
            notes: state.notes,
            holding,
            board,
        };

        if (state.bigBlind < state.smallBlind) {
            toast.error('Big Blind Amount should be greater or equal to Small Blind');
            return;
        }

        setState(prevState => ({
            ...prevState,
            loading: true
        }));

        try {
            if (prevHand?.id) {
                 await axiosInstance.patch(`${API_ENDPOINTS.SESSION_HAND}?id=${id}`, {
                    id: prevHand.id,
                    sessionId: parseInt(state.session),
                    handed: parseInt(state.handed.value),
                    sb: parseInt(state.smallBlind),
                    bb: parseInt(state.bigBlind),
                    ante: parseInt(state.ante),
                    stack: parseInt(state.stackSize),
                    position: state.selectedPosition.value,
                    notes: state.notes,
                    holding,
                    board,
                });
                navigate(`/Sessions/SessionCard/preflop/${prevHand.id}`);
            } else {
                const res = await axiosInstance.post(`${API_ENDPOINTS.SESSION_HAND}`, postData);
                navigate(`/Sessions/SessionCard/preflop/${res.data.id}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update results");
        } finally {
            setState(prev => ({
                ...prev,
                loading: false,
                cardSelection: {},
                boardSelection: {},
            }));
        }
    };



    const holdingReady = state.cardSelection.value1 && state.cardSelection.type1 && state.cardSelection.value2 && state.cardSelection.type2;

    return (
        <div className="bg-black w-full text-white font-sans p-6 mb-10 md:mb-0">
            {state.loading && (
                <ThreeDots
                    visible={true}
                    height="80"
                    width="80"
                    color="#1976d2"
                    radius="9"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClass="dots-loading additional-class"
                />
            )}
            <div className="flex flex-wrap md:flex-nowrap items-center mb-6">
                <button
                    className="border border-border hover:bg-gray-800 text-white text-xl mr-3 w-10 h-10 flex items-center justify-center rounded-lg"
                    onClick={handleBack}
                    type="button"
                >
                    <IoChevronBackOutline />
                </button>
                <h1 className="font-bold text-xl m-0">{prevHand?.id ? "Update Hand" : "Hand Setup"}</h1>
                <button
                    className="bg-secondary ml-auto w-full md:w-auto  mt-5 md:mt-0 justify-center   hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2  "
                    onClick={() => {
                        setShowNotes(prev => !prev);
                    }}
                >
                    <GrNotes className='w-4 h-4' />
                    Notes
                </button>
            </div>
            <div className="w-full mx-auto border border-border rounded-lg p-3 md:p-6 bg-primary">
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-wrap items-start gap-2 md:gap-10">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 md:flex-[2] w-full">
                            <div>
                                <label className="block mb-1">Handed</label>
                                <Select
                                    className="text-black"
                                    value={state.handed}
                                    onChange={handleHandedChange}
                                    options={handedOptions}
                                    isSearchable={false}
                                    styles={customStyles}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Small Blind</label>
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full bg-primary border border-border rounded-lg p-2 text-white"
                                    value={state.smallBlind}
                                    onChange={handleSmallBlindChange}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Big Blind</label>
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full bg-primary border border-border rounded-lg p-2 text-white"
                                    value={state.bigBlind}
                                    onChange={handleBigBlindChange}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Ante</label>
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full bg-primary border border-border rounded-lg p-2 text-white"
                                    value={state.ante}
                                    onChange={e => setState(prev => ({ ...prev, ante: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Stack Size</label>
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full bg-primary border border-border rounded-lg p-2 text-white"
                                    value={state.stackSize}
                                    onChange={e => setState(prev => ({ ...prev, stackSize: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Position</label>
                                <Select
                                    className="text-black"
                                    value={state.selectedPosition}
                                    onChange={handlePositionChange}
                                    options={state.position}
                                    isSearchable={false}
                                    styles={customStyles}
                                />
                            </div>

                        </div>
                        <div className="flex flex-col gap-2 md:gap-5  md:flex-1">
                            <CardSelector
                                count={2}
                                value={state.cardSelection}
                                onChange={handleCardSelectionChange}
                                label="Hero Cards"
                                usedCards={usedCards}
                            />
                            <CardSelector
                                count={5}
                                value={state.boardSelection}
                                onChange={handleBoardSelectionChange}
                                label="Board Cards (optional)"
                                usedCards={usedCards}
                            />
                        </div>
                    </div>
                    {showNotes &&
                        <div className=' fixed inset-0 z-[9999] flex items-center justify-center px-3 bg-black bg-opacity-60'>
                            <div className='max-w-[600px] mx-auto w-full border border-border bg-[#181818] rounded shadow-lg p-6'>
                                <label className="block mb-4 text-lg">Add Notes</label>
                                <textarea className='w-full bg-primary border border-border rounded-lg p-2 min-h-[150px] text-white' placeholder='Notes...'
                                    onChange={e => setState(prev => ({ ...prev, notes: e.target.value }))}
                                    value={state.notes}
                                ></textarea>
                                <div className="flex justify-end gap-4 mt-4">
                                    <button
                                        className="px-6 py-2 border border-gray-500 rounded bg-black text-white hover:bg-gray-800"
                                        onClick={() => setShowNotes(false)}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="bg-secondary  hover:opacity-75 px-6 py-2 rounded text-white flex items-center gap-2"
                                        onClick={() => setShowNotes(false)}
                                    >
                                        Add Note
                                    </button>
                                </div>
                            </div>
                        </div>
                    }

                    {holdingReady && (
                        <div className="flex justify-end mt-4">
                            <button
                                type="submit"
                                className="bg-secondary hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2"
                            >
                                {prevHand?.id ? "Next" : "Start"}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default AddHand;
